import { FlowEntityType, IFlowEntity } from '@/services/flow-entities/types'
import { IWizardCategory } from '@/services/wizard-categories/types'
import { XYPosition } from '@xyflow/react'
import { JsonSchema } from '../components/form/types'

export enum WizardBlockType {
    TRIGGER = 'trigger',
    ACTION = 'action',
    LOOP = 'loop',
    CONDITION_GROUP = 'condition_group',
    CONDITION = 'condition',
}

export interface IDynamicInputConfig {
    type: 'dynamic'
    sourceBlock: string
    outputField: string
}

export enum IWizardExecutionStatus {
    PENDING = 'pending',
    IN_PROGRESS = 'in_progress',
    COMPLETED = 'completed',
    FAILED = 'failed',
    WAITING = 'waiting',
    REVIEW_OUTPUT = 'review_output',
    CANCELLED = 'cancelled',
}

export interface IStaticInputConfig {
    type: 'static'
    value: string
}

export type IInputConfig = IDynamicInputConfig | IStaticInputConfig

export type ValueReference = {
    type: 'dynamic' | 'static'
    value: string
}

export interface ICondition {
    field: ValueReference
    operator: string
    value: ValueReference
}

export interface IWizardBlock {
    id: string
    name: string
    entity: IFlowEntity
    type: FlowEntityType
    wizard: IWizard
    _id: string
    next: string
    step: number
    interactive: boolean
    inputConfig: Record<string, any>
    conditions: ICondition[]
    createdAt: string
    lastExecutedBlock?: string | null
    failureInfo?: {
        failed: boolean
        reason: string | null
    } | null
    updatedAt: string
    label?: string
    test_output?: any
    position: XYPosition
    draggable: boolean
    review_output: boolean
    allow_custom_input_schema: boolean
    allow_custom_output_schema: boolean
    custom_input_schema: JsonSchema
    custom_output_schema: JsonSchema
    parentId?: string
    layoutOptions?: {
        [key: string]: string | number
    }
    [key: string]: unknown
}

export interface IWizardOwner {
    _id?: string
    id?: string
    name?: string
    email?: string
}

export interface IWizard {
    id: string
    name: string
    description: string
    slug: string
    org: string
    account: string
    product_id: string
    thumbnail: string
    status: string
    active: boolean
    internal: boolean
    bundle_only: boolean
    trigger: string
    blocks: IWizardBlock[]
    blockCount?: number
    _id: string
    createdAt: string
    updatedAt: string
    usage_count: number
    price: number
    category?: IWizardCategory
    sub_category?: IWizardCategory
    trigger_label?: string
    demoFree: boolean
    type: string
    seo: Record<string, any>
    rating: number
    chats: any[]
    /** Resolved owner from the admin list aggregation. */
    owner?: IWizardOwner
    /** Active execution count from the admin list aggregation. */
    activeRuns?: number
    /** Total execution count (lifetime) from the admin list aggregation. */
    totalRuns?: number
    /** ISO timestamp of the most recent execution, if any. */
    lastRunAt?: string | null
    /** Whether the current account has favorited this wizard. */
    favorited?: boolean
}

export interface IUserWizard {
    wizard: IWizard
    account: string
    bundle_price?: number
    id?: string
    order?: number
    uses?: number
    org: string
    usage_count: number
    available_count: number
    _id: string
    createdAt: string
    updatedAt: string
    user_wizard_bundle?: string
    conversation?: {
        role: 'assistant' | 'user'
        content: string
    }[]
    requirements?: {
        label: string
        value: string
    }[]
}

export interface IUserBundle {
    id: string
    name: string
    price: number
    product_id: string
    status: string
    updatedAt: string
    description: string
    seo: Record<string, any>
    wizard: IWizard
    wizards: IWizard[]
    bundle_wizards: IUserWizard[]
    _id: string
    createdAt: string
}

export interface IUserWizardBundle {
    bundle: IUserBundle
    account: string
    org: string
    usage_count: number
    wizards_data: IUserWizard[]
    user_wizards: IUserWizard[]
    bundle_wizards: IUserWizard[]
    _id: string
}

export type BlockOutput = {
    input: any
    output: any
    block: IWizardBlock
    error?: Record<string, any> | string
}

export type WizardExecutionAssetType =
    | 'Document'
    | 'Folder'
    | 'Email'
    | 'Video'
    | 'Image'
    | 'Attachment'
    | 'Report'
    | 'Dataset'

export interface IWizardExecutionAsset {
    _id: string
    execution: string
    wizard: string
    block: string
    assetType: WizardExecutionAssetType
    assetId: string
    name: string
    description?: string
    url?: string
    metadata?: Record<string, any>
    org: string
    account: string
    status: 'pending' | 'completed' | 'failed'
    error?: string
    createdAt: string
    updatedAt: string
}

export interface IWizardExecution {
    _id: string
    id: string
    name?: string | null
    notes?: string | null
    wizard: IWizard
    account: string
    org: string
    status: IWizardExecutionStatus
    currentStep: number
    blockData: Map<string, BlockOutput>
    createdAt: string
    updatedAt: string
    lastExecutedBlock: string | null
    currentPausedBlock: IWizardBlock | string | null
    outputMarkdown: string
    outputJson: any
    reports: string[]
    assets?: IWizardExecutionAsset[]
    failureReason: string | null
    uiContext?: {
        transitionFormType: 'wizard_form' | 'decision_gate'
        transitionFormId: string
    }
}
